import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { getPublicSiteSettings } from '../services/api';
import './Presentation.css';

const Presentation = () => {
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicSiteSettings()
      .then((res) => {
        if (res && res.data && typeof res.data === 'object') {
          setSiteSettings(res.data);
        }
      })
      .catch(() => setSiteSettings({ maintenance_mode: false, site_name: 'MUTU NDE ESPOIR ASBL', site_tagline: '', site_logo: '/logo.png', site_email: '', site_phone: '', site_address: '' }))
      .finally(() => setLoading(false));
  }, []);

  const name = siteSettings?.site_name || 'MUTU NDE ESPOIR ASBL';
  const tagline = siteSettings?.site_tagline || 'Prise en charge santé, juridique et sociale';
  const logo = siteSettings?.site_logo || '/logo.png';
  const maintenance = siteSettings?.maintenance_mode === true;

  if (loading) {
    return (
      <div className="presentation-page">
        <Navigation siteName={name} siteLogo={logo} />
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (maintenance) {
    return (
      <div className="presentation-page">
        <Navigation siteName={name} siteLogo={logo} />
        <section className="hero-section" style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="hero-content" style={{ textAlign: 'center' }}>
            <div className="hero-logo">
              <img src={logo} alt={name} className="logo-hero" onError={(e) => { e.target.src = '/logo.png'; }} />
            </div>
            <h1 className="hero-title">{name}</h1>
            <p className="hero-subtitle">Site en maintenance</p>
            <p className="hero-description">
              Nous effectuons des mises à jour. Merci de revenir dans quelques instants.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="presentation-page">
      <Navigation siteName={name} siteLogo={logo} />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-logo">
            <img src={logo} alt={name} className="logo-hero" onError={(e) => { e.target.src = '/logo.png'; }} />
          </div>
          <h1 className="hero-title">{name}</h1>
          <p className="hero-subtitle">{tagline}</p>
          <p className="hero-description">
            {siteSettings?.index_hero_description || 'Une mutuelle de solidarité dédiée à l\'amélioration de la qualité de vie de nos membres à travers des services de santé, d\'assistance juridique et de soutien social.'}
          </p>
          <div className="hero-buttons">
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <h2 className="section-title">{siteSettings?.index_about_title || 'Qui Sommes-Nous ?'}</h2>
          <div className="about-content">
            <div className="about-text">
              <p>{siteSettings?.index_about_p1 || `${name} est une association sans but lucratif basée à Kisangani, en République Démocratique du Congo. Notre mission est de fournir un accès équitable aux services de santé, d'assistance juridique et de soutien social à nos membres.`}</p>
              <p>{siteSettings?.index_about_p2 || 'Fondée sur les principes de solidarité et d\'entraide, notre organisation œuvre pour améliorer les conditions de vie de la communauté en offrant des services de qualité à des tarifs accessibles.'}</p>
            </div>
            <div className="about-image">
              <img src={siteSettings?.index_about_image || '/rdc.png'} alt="RDC" className="rdc-flag" onError={(e) => { e.target.src = '/rdc.png'; }} />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <h2 className="section-title">{siteSettings?.index_services_title || 'Nos Services'}</h2>
          <div className="services-grid">
            {Array.isArray(siteSettings?.index_services) && siteSettings.index_services.length > 0
              ? siteSettings.index_services.filter(s => s && (s.title || s.description)).map((s, i) => (
                  <div key={i} className="service-card">
                    <div className="service-icon">{s.icon || '•'}</div>
                    <h3 className="service-title">{s.title || ''}</h3>
                    <p className="service-description">{s.description || ''}</p>
                  </div>
                ))
              : (
                <>
                  <div className="service-card">
                    <div className="service-icon">🏥</div>
                    <h3 className="service-title">Prise en Charge Santé</h3>
                    <p className="service-description">Accès à des soins médicaux de qualité dans nos centres de santé partenaires. Couverture des consultations, examens et traitements essentiels.</p>
                  </div>
                  <div className="service-card">
                    <div className="service-icon">⚖️</div>
                    <h3 className="service-title">Assistance Juridique</h3>
                    <p className="service-description">Conseils juridiques et accompagnement dans vos démarches administratives et légales.</p>
                  </div>
                  <div className="service-card">
                    <div className="service-icon">🤝</div>
                    <h3 className="service-title">Soutien Social</h3>
                    <p className="service-description">Accompagnement social, orientation vers les services appropriés et soutien dans les situations difficiles.</p>
                  </div>
                </>
              )}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <h2 className="section-title">{siteSettings?.index_values_title || 'Nos Valeurs'}</h2>
          <div className="values-grid">
            {Array.isArray(siteSettings?.index_values) && siteSettings.index_values.length > 0
              ? siteSettings.index_values.filter(v => v && (v.title || v.description)).map((v, i) => (
                  <div key={i} className="value-item">
                    <h4>{v.title || ''}</h4>
                    <p>{v.description || ''}</p>
                  </div>
                ))
              : (
                <>
                  <div className="value-item"><h4>Solidarité</h4><p>Nous croyons en la force de l'entraide et de la solidarité communautaire.</p></div>
                  <div className="value-item"><h4>Transparence</h4><p>Gestion transparente et responsable de nos ressources et activités.</p></div>
                  <div className="value-item"><h4>Accessibilité</h4><p>Services accessibles à tous, sans discrimination, à des tarifs équitables.</p></div>
                  <div className="value-item"><h4>Qualité</h4><p>Engagement à fournir des services de qualité à nos membres.</p></div>
                </>
              )}
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section className="membership-section">
        <div className="container">
          <h2 className="section-title">{siteSettings?.index_membership_title || 'Devenez Membre'}</h2>
          <div className="membership-content">
            <div className="membership-benefits">
              <h3>{siteSettings?.index_membership_benefits_title || 'Avantages de l\'adhésion'}</h3>
              <ul className="benefits-list">
                {Array.isArray(siteSettings?.index_membership_benefits) && siteSettings.index_membership_benefits.length > 0
                  ? siteSettings.index_membership_benefits.filter(Boolean).map((text, i) => (
                      <li key={i}>✅ {typeof text === 'string' ? text : (text?.text ?? text?.label ?? '')}</li>
                    ))
                  : (
                    <>
                      <li>✅ Accès aux services de santé dans nos centres partenaires</li>
                      <li>✅ Assistance juridique pour vous et votre famille</li>
                      <li>✅ Soutien social et orientation</li>
                      <li>✅ Carte de membre avec code-barres</li>
                      <li>✅ Suivi de vos cotisations en ligne</li>
                      <li>✅ Application mobile disponible</li>
                    </>
                  )}
              </ul>
            </div>
            <div className="membership-cta">
              <p className="cta-text">
                {siteSettings?.index_membership_cta || 'Rejoignez notre communauté et bénéficiez de nos services de qualité.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <h2 className="section-title">Contactez-Nous</h2>
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-item">
                <h4>📍 Localisation</h4>
                <p>{siteSettings?.site_address || 'Kisangani, République Démocratique du Congo'}</p>
              </div>
              <div className="contact-item">
                <h4>📧 Email</h4>
                <p>{siteSettings?.site_email || 'contact@mutundeespoir.org'}</p>
              </div>
              <div className="contact-item">
                <h4>📱 Téléphone</h4>
                <p>{siteSettings?.site_phone || '+243 XXX XXX XXX'}</p>
              </div>
            </div>
            <div className="contact-actions">
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="presentation-footer">
        <div className="container">
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} {name}. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Presentation;

